import { makeStyles } from '@mui/styles'
import classNames from 'classnames'
import * as React from 'react'
import { useEffect, useRef, useState } from 'react'

type BoundaryEdge = keyof Pick<ClientRect, 'top' | 'bottom' | 'left' | 'right'>

interface Props {
  boundaryEdge: BoundaryEdge
  offset?: number
}

const DEFAULT_OFFSET = 16

function useSticky({
  boundaryEdge,
  offset = DEFAULT_OFFSET,
}: Props): [React.MutableRefObject<HTMLDivElement | null>, boolean] {
  const elementRef = useRef<HTMLDivElement>(null)
  const [isSticky, setIsSticky] = useState(false)

  const handleScroll = () => {
    // used to throttle the event since scroll events can fire at a high rate
    window.requestAnimationFrame(() => {
      if (!elementRef.current) return

      const elementRefBoundingClientRect = elementRef.current.getBoundingClientRect()
      const isParentOnOriginalPosition =
        elementRef.current.parentElement &&
        elementRef.current.parentElement.getBoundingClientRect()[boundaryEdge] >= offset
      const isElementTooBig = ['top', 'bottom'].includes(boundaryEdge)
        ? elementRefBoundingClientRect.height + offset >= window.innerHeight
        : elementRefBoundingClientRect.width + offset >= window.innerWidth

      if (isParentOnOriginalPosition || isElementTooBig) {
        setIsSticky(false)
      } else if (elementRefBoundingClientRect[boundaryEdge] <= offset) {
        setIsSticky(true)
      }
    })
  }

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [])

  return [elementRef, isSticky]
}

const useStyles = makeStyles({
  root: {
    width: '100%',
  },
  sticky: (props: Pick<Props, 'offset'>) => ({
    position: 'fixed',
    maxWidth: 'inherit',
    top: props.offset,
  }),
})

const Sticky: React.FC<React.PropsWithChildren<Props>> = ({ boundaryEdge, offset = DEFAULT_OFFSET, children }) => {
  const [containerRef, isSticky] = useSticky({ boundaryEdge, offset })
  const classes = useStyles({ offset })

  return (
    <div className={classNames({ [classes.sticky]: isSticky, [classes.root]: true })} ref={containerRef}>
      {children}
    </div>
  )
}

export default Sticky
